import {create} from "zustand"
import axios, {AxiosResponse} from "axios"
import {withAuth} from "../services/apiAuth"
import {READ_ONLY_REFUSED, isReadOnlyRole, onConsoleUserChange, useAuthStore} from "./authStore"

export interface CommandHistoryEntry {
	id: string
	user_email: string | null
	user_ID: string | null
	action: string
	description: string
	createdAt: string
	updated: string
}

export interface FetchHistoryParams {
	page?: number
	pageSize?: number
	sortField?: string
	sortOrder?: "asc" | "desc"
	actionFilter?: string
}

export interface PaginationMeta {
	page: number
	pageSize: number
	total: number
	totalPages: number
}

interface CommandHistoryState {
	entries: CommandHistoryEntry[]
	pagination: PaginationMeta
	isLoading: boolean
	error: string | null
	fetchHistory: (params?: FetchHistoryParams) => Promise<CommandHistoryEntry[]>
	updateDescription: (id: string, description: string) => Promise<void>
	clearError: () => void
	reset: () => void
}

interface FetchHistoryResponse {
	entries: CommandHistoryEntry[]
	pagination: PaginationMeta
}

interface UpdateDescriptionResponse {
	success: true
	entry: CommandHistoryEntry
}

interface ErrorResponse {
	error: string
}

const API_BASE_URL = process.env.REACT_APP_FIRMWARE_UPLOAD_API

const api = withAuth(
	axios.create({
		baseURL: API_BASE_URL,
		headers: {"Content-Type": "application/json"},
		timeout: 15000,
	}),
)

const DEFAULT_PAGINATION: PaginationMeta = {
	page: 1,
	pageSize: 20,
	total: 0,
	totalPages: 0,
}

export const useCommandHistoryStore = create<CommandHistoryState>()((set) => ({
	entries: [],
	pagination: DEFAULT_PAGINATION,
	isLoading: false,
	error: null,

	fetchHistory: async (params: FetchHistoryParams = {}) => {
		set({isLoading: true, error: null})

		try {
			const response: AxiosResponse<FetchHistoryResponse> = await api.post("", {
				action: "fetchCommandHistory",
				page: params.page ?? 1,
				pageSize: params.pageSize ?? 20,
				sortField: params.sortField ?? "createdAt",
				sortOrder: params.sortOrder ?? "desc",
				actionFilter: params.actionFilter ?? "",
			})

			const entries = response.data.entries ?? []
			const pagination = response.data.pagination ?? DEFAULT_PAGINATION
			set({entries, pagination, isLoading: false})
			return entries
		} catch (error) {
			console.error("fetchHistory error:", error)
			let message = "Unknown error occurred while fetching command history"
			if (axios.isAxiosError(error)) {
				if (error.response) {
					const data = error.response.data as ErrorResponse
					message = data.error || `Server error: ${error.response.status}`
				} else if (error.request) {
					message = "No response from server — check network connection or CORS configuration"
				} else {
					message = "Request configuration error"
				}
			}
			set({isLoading: false, error: message})
			throw new Error(message)
		}
	},

	updateDescription: async (id: string, description: string) => {
		// Read-only roles may read history but not edit it (the backend answers with 403 too).
		if (isReadOnlyRole(useAuthStore.getState().userProfile?.role)) throw new Error(READ_ONLY_REFUSED)
		try {
			const response: AxiosResponse<UpdateDescriptionResponse> = await api.post("", {
				action: "updateHistoryDescription",
				id,
				description,
			})

			const updatedEntry = response.data.entry
			set((state) => ({
				entries: state.entries.map((entry) =>
					entry.id === id
						? (updatedEntry ?? {...entry, description, updated: new Date().toISOString()})
						: entry,
				),
			}))
		} catch (error) {
			console.error("updateDescription error:", error)
			let message = "Unknown error occurred while updating description"
			if (axios.isAxiosError(error)) {
				if (error.response) {
					const data = error.response.data as ErrorResponse
					message = data.error || `Server error: ${error.response.status}`
				} else if (error.request) {
					message = "No response from server — check network connection or CORS configuration"
				} else {
					message = "Request configuration error"
				}
			}
			throw new Error(message)
		}
	},

	clearError: () => set({error: null}),

	reset: () => set({entries: [], pagination: DEFAULT_PAGINATION, isLoading: false, error: null}),
}))

// History is per user for viewers/developers: drop it on logout / user switch.
onConsoleUserChange(() => useCommandHistoryStore.getState().reset())
